import {Alert, Stack, Text} from '@mantine/core'
import type {ReactNode} from 'react'
import {useEffect, useMemo, useState} from 'react'

import {useDispatchYards} from 'src/dispatch/use-dispatch'
import {useJobAvailabilityData} from 'src/dispatch/use-job-availability-board'
import {useAssignableStockEquipment} from 'src/jobs/stock-equipment/use-stock-equipment'

import {
    allocationCountForRequirement,
    bookingDaysLabel,
    isDriverAllocationNotified,
    isRequirementSatisfied,
    needsDriverNotification,
} from './allocation'
import {BookingScopeControl} from './booking-scope-control'
import {DispatchAssignedResourceCard} from './dispatch-assigned-resource-card'
import classes from './dispatch-assignment-panel.module.css'
import {referenceDateForBoardJob} from './dispatch-board-resource-strip'
import {buildCandidatesByRequirement} from './dispatch-candidate-builders'
import {DrawerRequirementHeading} from './dispatch-drawer-typography'
import {type DispatchPickerCandidate, DispatchResourcePicker} from './dispatch-resource-picker'
import {isBorrowedDriver, sameDayBookingLabel} from './driver-fit'
import {buildDispatchPickerEmptyState} from './picker-empty-state'
import {applyFrozenRequirementOrder, releaseFrozenRequirementOrder} from './requirement-order'
import {
    groupAssignedAllocationsForDisplay,
    isStockEquipmentRequirement,
    maxQuantityForBulkAssign,
    stockAssignabilityReason,
} from './stock-equipment'
import type {
    BookingDays,
    DispatchTimelineData,
    JobAllocation,
    JobRequirement,
    UnassignedJob,
} from './types'
import {useBookingScope} from './use-booking-scope'

type DispatchAssignmentPanelProps = {
    active: boolean
    boardJob: UnassignedJob
    displayData: DispatchTimelineData
    onSubmit: (allocations: JobAllocation[]) => Promise<void>
    onReplaceAllocations: (allocations: JobAllocation[]) => Promise<void>
    onApplyStockDelta: (requirement: JobRequirement, delta: number) => Promise<void>
    onMarkDriverSent: (resourceId: string) => Promise<void>
    onCopyDriverLink?: (workerId: string) => Promise<void>
    onOpenBoardJob?: (jobId: string) => void
    bookByDay?: boolean
    oneDay?: boolean
    viewedDay?: string | null
}

function candidateTier(
    candidate: {
        group?: 'recommended' | 'available'
        equipmentTier?: 'matching' | 'other'
        bookedElsewhere?: boolean
    },
    requirementKind: JobRequirement['kind'],
): 'recommended' | 'available' | 'booked' {
    if (candidate.bookedElsewhere) {
        return 'booked'
    }
    if (requirementKind === 'worker') {
        return candidate.group === 'recommended' ? 'recommended' : 'available'
    }

    return candidate.equipmentTier === 'matching' ? 'recommended' : 'available'
}

function fitLabelForCandidate(
    tier: 'recommended' | 'available' | 'booked',
    requirementKind: JobRequirement['kind'],
): string | undefined {
    if (requirementKind !== 'worker' || tier !== 'recommended') {
        return undefined
    }
    return 'Good fit'
}

function toPickerCandidates(
    requirement: JobRequirement,
    candidates: Array<{
        resourceId: string
        resourceName: string
        group?: 'recommended' | 'available'
        equipmentTier?: 'matching' | 'other'
        bookedElsewhere?: boolean
        conflictJob?: {jobId: string; jobName: string}
        conflictRelease?: {date: string; time: string}
        availabilityMessage?: string
        reasons?: string[]
        stockQuantity?: boolean
        maxQuantity?: number
        rentalAssetTypeId?: string
    }>,
    assignedIds: Set<string>,
): DispatchPickerCandidate[] {
    return candidates
        .filter((candidate) => {
            if (candidate.stockQuantity) {
                return true
            }
            return !assignedIds.has(candidate.resourceId)
        })
        .map((candidate) => {
            const tier = candidateTier(candidate, requirement.kind)
            return {
                resourceId: candidate.resourceId,
                resourceName: candidate.resourceName,
                tier,
                fitLabel: fitLabelForCandidate(tier, requirement.kind),
                reasons: candidate.reasons ?? [],
                stockQuantity: candidate.stockQuantity,
                maxQuantity: candidate.maxQuantity,
                rentalAssetTypeId: candidate.rentalAssetTypeId,
                conflictJob: candidate.conflictJob,
                conflictRelease: candidate.conflictRelease,
                availabilityMessage: candidate.availabilityMessage,
            }
        })
}

function partitionRequirements(requirements: JobRequirement[]) {
    return {
        crew: requirements.filter((requirement) => requirement.kind === 'worker'),
        equipment: requirements.filter((requirement) => requirement.kind === 'rental_asset'),
    }
}

/**
 * Freeze requirement section order for the open job so assign/unassign (and any
 * panel remount after refetch) does not reshuffle crew/equipment sections.
 */
function useStableRequirements(
    jobId: string,
    requirements: JobRequirement[],
    active: boolean,
): JobRequirement[] {
    useEffect(() => {
        if (!active) {
            releaseFrozenRequirementOrder(jobId)
        }
    }, [active, jobId])

    return useMemo(() => {
        if (!active) {
            return requirements
        }
        return applyFrozenRequirementOrder(jobId, requirements)
    }, [active, jobId, requirements])
}

function CrewEquipmentGrid({
    requirements,
    renderRequirement,
}: {
    requirements: JobRequirement[]
    renderRequirement: (requirement: JobRequirement) => ReactNode
}) {
    const {crew, equipment} = useMemo(() => partitionRequirements(requirements), [requirements])

    return (
        <div className={classes['crewEquipmentGrid']}>
            <Stack
                gap="md"
                className={classes['crewColumn']}
                data-testid="crew-equipment-crew-column"
            >
                {crew.map(renderRequirement)}
            </Stack>
            <Stack
                gap="md"
                className={classes['equipmentColumn']}
                data-testid="crew-equipment-equipment-column"
            >
                {equipment.map(renderRequirement)}
            </Stack>
        </div>
    )
}

function RequirementAssignmentSection({
    requirement,
    boardJob,
    displayData,
    availabilityData = displayData,
    candidates,
    assignedIds,
    candidateReasonsById,
    isSubmitting,
    readOnly = false,
    markingSentResourceId = null,
    copyingLinkResourceId = null,
    bookingDays,
    onAssign,
    onRemoveStock,
    onRemove,
    onMarkDriverSent,
    onCopyDriverLink,
    onOpenBoardJob,
}: {
    requirement: JobRequirement
    boardJob: UnassignedJob
    displayData: DispatchTimelineData
    availabilityData?: DispatchTimelineData
    candidates: Array<{
        resourceId: string
        resourceName: string
        group?: 'recommended' | 'available'
        equipmentTier?: 'matching' | 'other'
        bookedElsewhere?: boolean
        conflictJob?: {jobId: string; jobName: string}
        reasons?: string[]
        stockQuantity?: boolean
        maxQuantity?: number
        rentalAssetTypeId?: string
    }>
    assignedIds: Set<string>
    candidateReasonsById: Map<string, string[]>
    isSubmitting: boolean
    readOnly?: boolean
    markingSentResourceId?: string | null
    copyingLinkResourceId?: string | null
    bookingDays?: BookingDays
    onAssign?: (candidate: DispatchPickerCandidate, quantity?: number) => void
    onRemoveStock?: (requirement: JobRequirement) => void
    onRemove?: (allocations: JobAllocation[]) => void
    onMarkDriverSent?: (resourceId: string) => void
    onCopyDriverLink?: (workerId: string) => void
    onOpenBoardJob?: (jobId: string) => void
}) {
    const isStockRequirement = isStockEquipmentRequirement(requirement, displayData.equipment)
    const satisfied = isRequirementSatisfied(boardJob, requirement)
    const stockSummary = useAssignableStockEquipment(
        boardJob.id,
        isStockRequirement ? requirement.rentalAssetTypeId : null,
        {enabled: !readOnly && isStockRequirement && !satisfied},
    )
    const assignableQuantity = stockSummary.data?.assignableQuantity ?? 0

    const pickerCandidates = useMemo(() => {
        const base = toPickerCandidates(requirement, candidates, assignedIds)
        if (!isStockRequirement) {
            return base
        }

        if (stockSummary.isLoading) {
            return base.map((candidate) =>
                candidate.stockQuantity
                    ? {...candidate, reasons: ['Checking availability…']}
                    : candidate,
            )
        }

        const stockBase = base.find((candidate) => candidate.stockQuantity)
        if (!stockBase) {
            return base
        }

        const freeMax = maxQuantityForBulkAssign(requirement, boardJob, assignableQuantity)

        if (assignableQuantity > 0 && freeMax > 0) {
            return [
                {
                    ...stockBase,
                    reasons: stockAssignabilityReason(assignableQuantity),
                    maxQuantity: freeMax,
                },
            ]
        }

        return base.filter((candidate) => !candidate.stockQuantity)
    }, [
        assignableQuantity,
        assignedIds,
        boardJob,
        candidates,
        isStockRequirement,
        requirement,
        stockSummary.isLoading,
    ])

    const assignedForRequirement = boardJob.allocations.filter(
        (allocation) => allocation.requirementId === requirement.id,
    )
    const assignedDisplay = useMemo(
        () =>
            groupAssignedAllocationsForDisplay(
                requirement,
                boardJob,
                assignedForRequirement,
                displayData.equipment,
            ),
        [assignedForRequirement, boardJob, displayData.equipment, requirement],
    )
    const {data: yards = []} = useDispatchYards()
    function borrowedFrom(resourceId: string): string | undefined {
        const resource = displayData.resources.find((entry) => entry.id === resourceId)
        if (requirement.kind !== 'worker' || !resource || !isBorrowedDriver(resource, boardJob)) {
            return undefined
        }
        return yards.find((yard) => yard.id === resource.homeYardId)?.name ?? 'another yard'
    }
    const count = allocationCountForRequirement(boardJob, requirement.id)
    const referenceDate = referenceDateForBoardJob(boardJob, displayData)
    const maxBulkQuantity = isStockRequirement
        ? maxQuantityForBulkAssign(requirement, boardJob, assignableQuantity)
        : 1
    const emptyDriverSlots =
        requirement.kind === 'worker' && !readOnly && !satisfied
            ? Math.max(0, requirement.quantity - count)
            : 0

    const hasAssignableCandidate = pickerCandidates.some((candidate) => candidate.tier !== 'booked')
    const availabilityHint =
        !readOnly && !satisfied && !hasAssignableCandidate
            ? buildDispatchPickerEmptyState({
                  requirement,
                  job: boardJob,
                  data: displayData,
                  stockAssignableQuantity: isStockRequirement ? assignableQuantity : null,
              }).hint
            : null

    const picker = (
        <DispatchResourcePicker
            requirement={requirement}
            boardJob={boardJob}
            candidates={pickerCandidates}
            displayData={availabilityData}
            referenceDate={referenceDate}
            excludeJobId={boardJob.id}
            bookingDays={bookingDays}
            disabled={isSubmitting || stockSummary.isLoading}
            maxBulkQuantity={maxBulkQuantity}
            onAssign={onAssign!}
            onOpenBoardJob={onOpenBoardJob}
        />
    )

    return (
        <section
            className={classes['requirementSection']}
            data-testid={`requirement-section-${requirement.id}`}
        >
            <DrawerRequirementHeading
                label={requirement.label}
                count={count}
                quantity={requirement.quantity}
                satisfied={satisfied}
                availabilityHint={availabilityHint}
                onSiteQuantity={requirement.onSiteQuantity}
            />

            {assignedDisplay.length > 0 || emptyDriverSlots > 0 ? (
                <Stack gap="xs">
                    {assignedDisplay.map((entry) =>
                        entry.type === 'group' ? (
                            <DispatchAssignedResourceCard
                                key={`group-${entry.resourceName}`}
                                resourceId={
                                    entry.stockQuantity
                                        ? (entry.rentalAssetTypeId ?? entry.resourceName)
                                        : (entry.allocations[0]?.resourceId ?? entry.resourceName)
                                }
                                resourceName={entry.resourceName}
                                quantity={entry.count}
                                requirement={requirement}
                                displayData={displayData}
                                onRemove={
                                    !onRemove && !onRemoveStock
                                        ? undefined
                                        : () => {
                                              if (entry.stockQuantity) {
                                                  onRemoveStock?.(requirement)
                                                  return
                                              }
                                              onRemove?.(entry.allocations)
                                          }
                                }
                                disabled={isSubmitting}
                            />
                        ) : (
                            <DispatchAssignedResourceCard
                                key={entry.allocation.resourceId}
                                resourceId={entry.allocation.resourceId}
                                resourceName={entry.allocation.resourceName}
                                jobPageHref={`/jobs/${boardJob.id}`}
                                requirement={requirement}
                                displayData={displayData}
                                fitReasons={candidateReasonsById.get(entry.allocation.resourceId)}
                                borrowedFrom={borrowedFrom(entry.allocation.resourceId)}
                                alsoBookedLabel={
                                    requirement.kind === 'worker'
                                        ? sameDayBookingLabel(
                                              entry.allocation.resourceId,
                                              boardJob,
                                              availabilityData,
                                          )
                                        : undefined
                                }
                                bookedDaysLabel={
                                    requirement.kind === 'worker'
                                        ? bookingDaysLabel(entry.allocation.days, boardJob.schedule)
                                        : undefined
                                }
                                sent={isDriverAllocationNotified(entry.allocation)}
                                isMarkingSent={
                                    markingSentResourceId === entry.allocation.resourceId
                                }
                                onMarkSent={
                                    requirement.kind === 'worker' &&
                                    onMarkDriverSent &&
                                    needsDriverNotification(entry.allocation)
                                        ? () => onMarkDriverSent(entry.allocation.resourceId)
                                        : undefined
                                }
                                isCopyingLink={
                                    copyingLinkResourceId === entry.allocation.resourceId
                                }
                                onCopyLink={
                                    requirement.kind === 'worker' &&
                                    onCopyDriverLink &&
                                    !entry.allocation.isPlaceholder
                                        ? () => onCopyDriverLink(entry.allocation.resourceId)
                                        : undefined
                                }
                                onRemove={
                                    !onRemove ? undefined : () => onRemove([entry.allocation])
                                }
                                disabled={isSubmitting}
                            />
                        ),
                    )}
                    {emptyDriverSlots > 0
                        ? Array.from({length: emptyDriverSlots}, (_, slotIndex) => (
                              <DispatchResourcePicker
                                  key={`${requirement.id}-slot-${slotIndex}`}
                                  requirement={requirement}
                                  boardJob={boardJob}
                                  candidates={pickerCandidates}
                                  displayData={availabilityData}
                                  referenceDate={referenceDate}
                                  excludeJobId={boardJob.id}
                                  bookingDays={bookingDays}
                                  disabled={isSubmitting}
                                  slotIndex={slotIndex}
                                  onAssign={onAssign!}
                                  onOpenBoardJob={onOpenBoardJob}
                              />
                          ))
                        : null}
                </Stack>
            ) : readOnly ? (
                <Text size="sm" c="dimmed">
                    None assigned
                </Text>
            ) : null}

            {!readOnly && !satisfied && requirement.kind !== 'worker' ? picker : null}
        </section>
    )
}

type DispatchAssignedResourcesPanelProps = {
    boardJob: UnassignedJob
    displayData: DispatchTimelineData
    onRemove?: (allocations: JobAllocation[]) => void
    onRemoveStock?: (requirement: JobRequirement) => void
}

/** Read-only requirement grid with assigned resource cards. */
export function DispatchAssignedResourcesPanel({
    boardJob,
    displayData,
    onRemove,
    onRemoveStock,
}: DispatchAssignedResourcesPanelProps) {
    const assignedIds = useMemo(
        () => new Set(boardJob.allocations.map((allocation) => allocation.resourceId)),
        [boardJob.allocations],
    )
    const requirements = useStableRequirements(boardJob.id, boardJob.requirements, true)

    return (
        <Stack gap="md" data-testid="dispatch-assigned-resources-panel">
            <CrewEquipmentGrid
                requirements={requirements}
                renderRequirement={(requirement) => (
                    <RequirementAssignmentSection
                        key={requirement.id}
                        requirement={requirement}
                        boardJob={boardJob}
                        displayData={displayData}
                        candidates={[]}
                        assignedIds={assignedIds}
                        candidateReasonsById={new Map()}
                        isSubmitting={false}
                        readOnly
                        onRemove={onRemove}
                        onRemoveStock={onRemoveStock}
                    />
                )}
            />
        </Stack>
    )
}

export function DispatchAssignmentPanel({
    active,
    boardJob,
    displayData,
    onSubmit,
    onReplaceAllocations,
    onApplyStockDelta,
    onMarkDriverSent,
    onCopyDriverLink,
    onOpenBoardJob,
    bookByDay = false,
    oneDay = false,
    viewedDay = null,
}: DispatchAssignmentPanelProps) {
    const [submittingRequirementId, setSubmittingRequirementId] = useState<string | null>(null)
    const [replacingAllocationIds, setReplacingAllocationIds] = useState<Set<string>>(new Set())
    const [markingSentResourceId, setMarkingSentResourceId] = useState<string | null>(null)
    const [copyingLinkResourceId, setCopyingLinkResourceId] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const booking = useBookingScope({job: boardJob, active, bookByDay, oneDay, viewedDay})

    const assignedIds = useMemo(
        () => new Set(boardJob.allocations.map((allocation) => allocation.resourceId)),
        [boardJob.allocations],
    )
    const requirements = useStableRequirements(boardJob.id, boardJob.requirements, active)

    const availabilityData = useJobAvailabilityData(boardJob, displayData, active)

    const candidatesByRequirement = useMemo(
        () => buildCandidatesByRequirement(boardJob, availabilityData, booking.pendingDays),
        [availabilityData, boardJob, booking.pendingDays],
    )

    const candidateReasonsById = useMemo(() => {
        const reasons = new Map<string, string[]>()
        for (const candidates of candidatesByRequirement.values()) {
            for (const candidate of candidates) {
                if (candidate.reasons?.length) {
                    reasons.set(candidate.resourceId, candidate.reasons)
                }
            }
        }
        return reasons
    }, [candidatesByRequirement])

    useEffect(() => {
        if (!active) {
            setSubmittingRequirementId(null)
            setReplacingAllocationIds(new Set())
            setMarkingSentResourceId(null)
            setCopyingLinkResourceId(null)
            setSubmitError(null)
        }
    }, [active])

    async function handleCopyDriverLink(workerId: string) {
        if (copyingLinkResourceId != null || !onCopyDriverLink) {
            return
        }

        setCopyingLinkResourceId(workerId)
        setSubmitError(null)
        try {
            await onCopyDriverLink(workerId)
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'Could not copy driver link')
        } finally {
            setCopyingLinkResourceId(null)
        }
    }

    async function handleMarkDriverSent(resourceId: string) {
        if (
            markingSentResourceId != null ||
            copyingLinkResourceId != null ||
            submittingRequirementId != null
        ) {
            return
        }

        setMarkingSentResourceId(resourceId)
        setSubmitError(null)
        try {
            await onMarkDriverSent(resourceId)
        } catch (error) {
            setSubmitError(
                error instanceof Error ? error.message : 'Could not mark driver notified',
            )
        } finally {
            setMarkingSentResourceId(null)
        }
    }

    async function handleRemove(allocationsToRemove: JobAllocation[]) {
        if (submittingRequirementId != null || replacingAllocationIds.size > 0) {
            return
        }

        const removeKeys = new Set(
            allocationsToRemove.map(
                (allocation) => `${allocation.requirementId}:${allocation.resourceId}`,
            ),
        )
        const nextAllocations = boardJob.allocations.filter(
            (allocation) => !removeKeys.has(`${allocation.requirementId}:${allocation.resourceId}`),
        )

        setReplacingAllocationIds(
            new Set(allocationsToRemove.map((allocation) => allocation.resourceId)),
        )
        setSubmitError(null)
        try {
            await onReplaceAllocations(nextAllocations)
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'Could not update assignment')
        } finally {
            setReplacingAllocationIds(new Set())
        }
    }

    async function handleRemoveStock(requirement: JobRequirement) {
        if (submittingRequirementId != null || replacingAllocationIds.size > 0) {
            return
        }

        const assignedQuantity = requirement.assignedQuantity ?? 0
        if (assignedQuantity <= 0 || !requirement.rentalAssetTypeId) {
            return
        }

        setSubmittingRequirementId(requirement.id)
        setSubmitError(null)
        try {
            await onApplyStockDelta(requirement, -assignedQuantity)
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'Could not update assignment')
        } finally {
            setSubmittingRequirementId(null)
        }
    }

    async function handleAssign(
        requirement: JobRequirement,
        candidate: DispatchPickerCandidate,
        quantity = 1,
    ) {
        if (submittingRequirementId != null || replacingAllocationIds.size > 0) {
            return
        }

        if (candidate.stockQuantity && requirement.rentalAssetTypeId) {
            const assignCount = Math.max(quantity, 1)
            setSubmittingRequirementId(requirement.id)
            setSubmitError(null)
            try {
                await onApplyStockDelta(requirement, assignCount)
            } catch (error) {
                setSubmitError(error instanceof Error ? error.message : 'Could not save assignment')
            } finally {
                setSubmittingRequirementId(null)
            }
            return
        }

        const allocations: JobAllocation[] = [
            {
                requirementId: requirement.id,
                resourceId: candidate.resourceId,
                resourceName: candidate.resourceName,
                kind: requirement.kind,
                ...(requirement.kind === 'worker' && booking.pendingDays
                    ? {days: booking.pendingDays}
                    : {}),
            },
        ]

        setSubmittingRequirementId(requirement.id)
        setSubmitError(null)
        try {
            await onSubmit(allocations)
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'Could not save assignment')
        } finally {
            setSubmittingRequirementId(null)
        }
    }

    return (
        <Stack gap="md" data-testid="dispatch-assignment-panel">
            {booking.day ? (
                <BookingScopeControl
                    jobId={boardJob.id}
                    scope={booking.scope}
                    onScopeChange={booking.setScope}
                    day={booking.day}
                    days={booking.days}
                    onDayChange={booking.setDay}
                />
            ) : null}
            <CrewEquipmentGrid
                requirements={requirements}
                renderRequirement={(requirement) => (
                    <RequirementAssignmentSection
                        key={requirement.id}
                        requirement={requirement}
                        boardJob={boardJob}
                        displayData={displayData}
                        availabilityData={availabilityData}
                        candidates={candidatesByRequirement.get(requirement.id) ?? []}
                        assignedIds={assignedIds}
                        candidateReasonsById={candidateReasonsById}
                        isSubmitting={
                            submittingRequirementId === requirement.id ||
                            boardJob.allocations.some(
                                (allocation) =>
                                    allocation.requirementId === requirement.id &&
                                    replacingAllocationIds.has(allocation.resourceId),
                            )
                        }
                        markingSentResourceId={markingSentResourceId}
                        copyingLinkResourceId={copyingLinkResourceId}
                        bookingDays={booking.pendingDays}
                        onAssign={(candidate, quantity) =>
                            void handleAssign(requirement, candidate, quantity)
                        }
                        onRemove={(allocations) => void handleRemove(allocations)}
                        onRemoveStock={(stockRequirement) =>
                            void handleRemoveStock(stockRequirement)
                        }
                        onMarkDriverSent={(resourceId) => void handleMarkDriverSent(resourceId)}
                        onCopyDriverLink={
                            onCopyDriverLink
                                ? (workerId) => void handleCopyDriverLink(workerId)
                                : undefined
                        }
                        onOpenBoardJob={onOpenBoardJob}
                    />
                )}
            />

            {submitError ? (
                <Alert color="red" title="Could not save assignment">
                    {submitError}
                </Alert>
            ) : null}
        </Stack>
    )
}
